import { Router } from '@angular/router';
import { VcUnitData } from './model/VcUnitData';
import { SearchService } from './service/search.service';
import { Component, Inject, ViewChild, OnInit, Input } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from "@angular/material/table";
import { SelectionModel } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { DialogData } from './model/dialogData';
import { MatSelectChange } from '@angular/material/select';
import { LoggerService } from 'src/shared/logger/logger.service';
import { Levels } from 'src/shared/logger/levels.enum';

@Component({
  selector: 'search-dialog',
  templateUrl: './dialog.search.component.html',
  styleUrls: ['./dialog.search.component.css'],
  providers: [SearchService]
})
export class DialogSearchComponent implements OnInit {
  //displayed columns for the table
  displayedColumns: string[] = ['checkBox', 'system_name', 'system_mac_address', 'system_ip', 'sip_registration_uri'];
  //these are the keys available that the name show in the dropdown list and we send the value to the api.
  keysAvailable: { text, value }[] = [
    { text: 'Name', value: 'system_name' }, { text: 'IP', value: 'system_ip' }, { text: 'MAC Address', value: 'system_mac_address' },
    { text: 'Time Zone', value: 'system_time_zone' }, { text: 'H323 Gatekeeper Address', value: 'h323_gatekeeper_address' },
    { text: 'H323 Gatekeeper Status ', value: 'h323_gatekeeper_status' }, { text: 'H323 Gatekeeper Mode', value: 'h323_gatekeeper_mode' },
    { text: 'SIP Proxy Address', value: 'sip_proxy_address' }, { text: 'SIP Proxy Status', value: 'sip_proxy_address_status' },
    { text: 'SIP Registration Status', value: 'sip_registration_status' }, { text: 'SIP Registration URI', value: 'sip_registration_uri' },
    { text: 'DNS Server 1', value: 'dns_server1' }, { text: 'DNS Server 2', value: 'dns_server2' },
    { text: 'Serial Number', value: 'serial_number' }, { text: 'NTP Server 1', value: 'ntp_server1' },
    { text: 'Product Id', value: 'product_id' }, { text: 'Software Name', value: 'software_name' }, { text: 'Software Version', value: 'software_version' }
  ]
  //the data that fills the table
  tableData: MatTableDataSource<DialogData>;
  //this is the message that shown if the table is not shown
  initialMessageAndNoData: string = 'Search from the dropdown list with the requested value.';
  //the message color according to the condition that have been met
  initialMessageColor: string = 'black';
  //this is the user choice from the drop down list and it stores the value corresponding to his selected text from keysAvailable array
  itemSelectedFromList: string = null;
  //this is the user inputted text to be used to send to the api and fetch the search result
  searchedInputText: string = null;
  //the array that stores the user chosen units ids.
  selectedItems: VcUnitData[] = [];
  //input to enable or disable multiple select in the table
  @Input() allowMultipleSelect: boolean = false;
  selection: SelectionModel<DialogData>;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  //this array is to be used to check the route and close the confirm button if the url does not match one of them.
  private allowedAccessRoutes: string[] = ['/templatemanager', '/softwareupdate', '/systemprofile', '/systemprofileV2'];
  //boolean to control the existence of the confirm button
  confirmBtnCheck: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<DialogSearchComponent>,
    public searchService: SearchService,
    private loggerService: LoggerService,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: DialogData[]) {
  }

  ngOnInit(): void {
    this.tableData = new MatTableDataSource([]);
    this.selection = new SelectionModel<DialogData>(this.allowMultipleSelect, []);
    if (this.allowedAccessRoutes.includes(this.router.url)) {
      this.confirmBtnCheck = true;
    } else {
      this.confirmBtnCheck = false;
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.tableData.filter = filterValue.trim().toLowerCase();
    if (this.tableData.paginator) {
      this.tableData.paginator.firstPage();
    }
  }

  checkboxLabel(row?: DialogData): void {
    if (this.selection.isSelected(row)) {
      if (!this.allowMultipleSelect) {
        this.selectedItems = [];
      }
      this.selectedItems.push(new VcUnitData(row.id, row.parent_id, row.parent_name));
    } else {
      if (!this.allowMultipleSelect && this.selectedItems.includes(new VcUnitData(row.id, row.parent_id, row.parent_name))) {
        this.selectedItems.pop();
      } else {
        const elementIndex = this.selectedItems.findIndex(i => i.id == row.id);
        if (elementIndex != -1) this.selectedItems.splice(elementIndex, 1);
      }
    }
  }

  selectionChange(event: MatSelectChange) {
    if (event.value) {
      // these lines seems to be working fine because in the case that he changes from the dropdown list whilst there were data
      //in the table the presentation is not correct so this resets all and adjust the message color and text.
      this.resetTable();
      this.initialMessageAndNoData = 'Search from the dropdown list with the requested value.';
      this.initialMessageColor = 'black';
      this.itemSelectedFromList = event.value;
    }
  }


  fetchData() {
    //reset table first
    this.resetTable();

    //case wrong input
    if (!this.itemSelectedFromList || this.searchedInputText == null || this.searchedInputText.length == 0) {
      this.initialMessageColor = 'red';
      this.initialMessageAndNoData = "Invalid input search data."
      return;
    }

    //send to the api
    this.searchService.postSearchData(this.itemSelectedFromList, this.searchedInputText).subscribe(items => {
      if (items.length == 0) {
        this.initialMessageColor = 'blue';
        this.initialMessageAndNoData = "No data were found matching your search";
        return;
      }
      this.data = items;
      this.tableData = new MatTableDataSource(this.data);
      this.tableData.paginator = this.paginator;
    }, err => {
      this.loggerService.log("DialogSearchComponent", err.message, Levels.error)

      this.initialMessageColor = 'red';
      this.initialMessageAndNoData = "A problem occurred while fetching data, please try again later.";
    })
  }

  // resets data in table, and adjust the logic of the existence of the search_result column or not in the table
  resetTable() {
    if (this.displayedColumns.includes(this.itemSelectedFromList)) {
      if (this.displayedColumns.includes('search_result')) {
        this.displayedColumns.pop();
      }
    } else {
      if (!this.displayedColumns.includes('search_result')) {
        this.displayedColumns.push('search_result');
      }
    }
    this.clearSelection();
    this.data = [];
    this.tableData = new MatTableDataSource(this.data);
    this.tableData.paginator = this.paginator;
    this.selectedItems = [];
  }
  //return the correct text for the search_result column if it exists
  fillText(): string {
    const result = this.keysAvailable.find(i => i.value == this.itemSelectedFromList).text;
    return result;
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.tableData.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected() ? this.clearSelection() : this.selectAll();
  }

  clearSelection() {
    this.selection.clear();
    this.selectedItems = [];
  }

  selectAll() {
    this.tableData.data.forEach(row => {
      this.selection.select(row);
      this.selectedItems.push(new VcUnitData(row.id, row.parent_id, row.parent_name));
    });
  }
  //fires when cancel is clicked
  onNoClick(): void {
    this.dialogRef.close();
  }

  //fires when confirm is clicked
  onYesClick(): void {
    this.dialogRef.close(this.selectedItems);
  }

}
